import type { TFunction } from 'i18next'
import type { DataSource } from '@/src/renderer/lib/dataSource'
import type { CreateTask, LifeSpace } from '@/src/renderer/lib/db/models'
import { formatImportedFrom } from '@/src/renderer/lib/import/importedFrom'

/** Belohnung aus dem Katalog an „Hake mich ab“: Barista-Set, „Einen Kaffee zubereiten“. */
export const INTRO_REWARD_ID = '36191bd3-6088-40ef-b55f-d329c164a85d'

const SAMPLE_PREFIX = 'intro:sample:'

/** Die Kennung des Beispielprojekts, wenn es noch lebt — sonst `null`. */
export async function findSampleProject(source: DataSource): Promise<string | null> {
  const { projects } = await source.findImported('intro')
  for (const [origin, row] of projects) if (origin.startsWith(SAMPLE_PREFIX) && !row.deleted) return row.id
  return null
}

/** Eine offene Aufgabe ohne Tag, Ruhen oder Wiederholung — Demnächst, Ruhend und Wiederkehrend bleiben leer. */
export function blankTask(title: string, lifeSpace: LifeSpace): CreateTask {
  return {
    title, description: null, status: 'todo', attention: null, category: 'other', dueDate: null, completedAt: null,
    estimatedDuration: null, actualDuration: null, tags: [], parentTaskId: null, pausedAt: null, lifeSpace,
  }
}

/**
 * **Beispielprojekt** „Willkommen bei HappyTask“ (#323, Variante G): sofort nach den Folien, ohne Angebot.
 * Vier Abschnitte wie die Bereiche der Einführung, drei Zettel auf der Pinnwand. Anders als die Testdaten
 * ohne lose Notizen (in Kurz notiert liegen die Lernaufgaben) und ohne Tag, Ruhen, Wiederholung (Sven,
 * 12.10.2026: „In Demnächst dürften noch keine Aufgaben sein“).
 *
 * Nur über die `DataSource`, also mit Sync wie alles andere. Jede Zeile trägt `intro:sample:<lauf>:…`.
 * Gibt es schon eins, bleibt es bei dem.
 */
export async function writeSampleProject(source: DataSource, { t, lifeSpace, quickAddKeys }: {
  t: TFunction
  lifeSpace: LifeSpace
  quickAddKeys: string
}): Promise<string> {
  const existing = await findSampleProject(source)
  if (existing) return existing

  // Der Lauf trennt ein neu angelegtes Beispielprojekt von einem gelöschten — `findImported` kennt je Marke eine Zeile.
  const run = Date.now().toString(36)
  let counter = 0
  const mark = (what: string) => formatImportedFrom('intro', `sample:${run}:${what}${what === 'project' ? '' : `:${++counter}`}`)

  const project = await source.createProject({
    name: t('intro.sample.project'), description: null, color: 'var(--space-500)', status: 'active',
    startDate: null, endDate: null, progress: 0, lifeSpace, importedFrom: mark('project'),
  })

  const sections: Array<[string, Array<{ key: string; rewardId?: string }>]> = [
    ['capture', [{ key: 'quickadd' }, { key: 'clarify' }]],
    ['plan', [{ key: 'dentist' }, { key: 'taxes' }, { key: 'plants' }]],
    ['organize', [{ key: 'lifespace' }, { key: 'board' }]],
    ['do', [{ key: 'focus' }, { key: 'done', rewardId: INTRO_REWARD_ID }]],
  ]
  for (const [sectionKey, tasks] of sections) {
    const section = await source.createSection({ title: t(`intro.sample.sections.${sectionKey}`), projectId: project.id, importedFrom: mark('section') })
    for (const { key, rewardId } of tasks) {
      await source.createTask({
        ...blankTask(t(`intro.sample.tasks.${key}`, { qa: quickAddKeys }), lifeSpace),
        projectId: project.id, sectionId: section.id, rewardId: rewardId ?? null, importedFrom: mark('task'),
      })
    }
  }

  const notes = [
    { content: t('intro.sample.notes.ideas'), x: 48, y: 48, color: 'yellow', rotation: -3 },
    { content: t('intro.sample.notes.task'), x: 264, y: 88, color: 'blue', rotation: 4 },
  ]
  for (const note of notes) {
    await source.createCanvasNote({ kind: 'note', ...note, width: 168, height: 168, projectId: project.id })
  }
  await source.createCanvasNote({ kind: 'memo', content: t('intro.sample.notes.memo'), x: 120, y: 280, width: 260, height: 140, projectId: project.id })

  return project.id
}
